/**
 * RequestLimitModal
 * Client-facing form to request a higher monthly usage cap.
 * Submits via the `requestLimitRaise` callable which writes a Firestore record
 * and emails info@jadenterprises.org.
 */

import { useState, FormEvent } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';

interface Props {
  defaultName: string;
  defaultEmail: string;
  defaultPhone?: string;
  onClose: () => void;
}

export function RequestLimitModal({ defaultName, defaultEmail, defaultPhone = '', onClose }: Props) {
  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [phone, setPhone] = useState(defaultPhone);
  const [requestedVolume, setRequestedVolume] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Client-side validation
    if (!name.trim()) { setError('Name is required'); return; }
    if (!phone.trim()) { setError('Phone is required'); return; }
    if (!email.trim() || !email.includes('@')) { setError('Valid email is required'); return; }
    const vol = parseInt(requestedVolume, 10);
    if (!Number.isFinite(vol) || vol <= 0) { setError('Requested monthly volume must be a positive number'); return; }

    setSubmitting(true);
    try {
      const fn = httpsCallable(functions, 'requestLimitRaise');
      await fn({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        requestedMonthlyVolume: vol,
        notes: notes.trim(),
      });
      setSuccess(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError('Request failed: ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" style={overlayStyle}>
      <div className="modal-content" style={contentStyle}>
        <h2 style={{ margin: '0 0 16px', color: 'var(--gold)' }}>Request Higher Monthly Limit</h2>

        {success ? (
          <div>
            <div style={{ padding: '16px', marginBottom: '16px', borderRadius: '8px', background: 'var(--green-bg)', color: 'var(--green)', border: '1px solid rgba(16,185,129,0.3)' }}>
              <strong>Request submitted.</strong> We&rsquo;ll review and reply by email.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onClose} className="btn btn-primary">Close</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <p style={{ color: 'var(--gray-300)', marginBottom: '16px', fontSize: '0.9rem', lineHeight: 1.6 }}>
              You&rsquo;ve reached your current monthly limit. Submit this form to request a higher cap &mdash;
              we&rsquo;ll review and reply by email.
            </p>

            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label>Name *</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} required disabled={submitting} />
            </div>
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label>Phone *</label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} required disabled={submitting} />
            </div>
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label>Email *</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required disabled={submitting} />
            </div>
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label>Requested Monthly Volume *</label>
              <input
                type="number"
                value={requestedVolume}
                onChange={e => setRequestedVolume(e.target.value)}
                placeholder="e.g. 50000"
                required
                min="1"
                disabled={submitting}
              />
              <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginTop: '4px' }}>
                Number of calls or contacts you need per month
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label>Additional notes (optional)</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Tell us about your use case"
                rows={3}
                disabled={submitting}
              />
            </div>

            {error && (
              <div style={{ padding: '12px', marginBottom: '16px', borderRadius: '6px', background: 'var(--red-bg)', color: 'var(--red)', border: '1px solid rgba(239,68,68,0.3)' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose} disabled={submitting} className="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={submitting} className="btn btn-primary">
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0, 0, 0, 0.7)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '16px',
};

const contentStyle: React.CSSProperties = {
  background: 'var(--bg-primary, #1e1e1e)', borderRadius: '12px',
  padding: '32px', maxWidth: '480px', width: '100%',
  border: '1px solid rgba(200, 168, 78, 0.3)',
  maxHeight: '90vh', overflowY: 'auto',
};
